// KNPPLZ — game data: weapons, passives, enemies, meta upgrades

export const CONFIG = {
  width: 480,
  height: 720,
  bgColor: 0x05050a,
  playerStartHp: 5,
  playerStartSpeed: 220,
  playerSize: 22,
  playerY: 640,
  playerMoveBandTop: 64, // just below the HUD strip
  // Starts near the ship's own size; the Tractor Field passive and
  // Magnetic Coils meta upgrade scale it up multiplicatively from here.
  pickupBase: 24,
  xpToLevel: (lvl) => Math.floor(5 + lvl * 4 + Math.pow(lvl, 1.5) * 2),
};

// ---------- WEAPONS ----------
export const WEAPONS = {
  blaster: {
    name: 'Pulse Blaster',
    color: 0x66ddff,
    maxLevel: 8,
    descs: [
      'Auto-fire a forward bolt.',
      '+1 bolt per volley.',
      '+25% fire rate.',
      '+1 bolt per volley.',
      'Bolts pierce 1 enemy.',
      '+25% damage.',
      '+1 bolt per volley.',
      'Bolts pierce +1.',
    ],
    cooldown(level) {
      const base = 360;
      const rate = (level >= 3 ? 1.25 : 1) * (level >= 6 ? 1.25 : 1);
      return base / rate;
    },
    count(level) {
      return 1 + (level >= 2 ? 1 : 0) + (level >= 4 ? 1 : 0) + (level >= 7 ? 1 : 0);
    },
    damage(level) {
      return 4 * (level >= 6 ? 1.25 : 1);
    },
    pierce(level) {
      return (level >= 5 ? 1 : 0) + (level >= 8 ? 1 : 0);
    },
  },
  spread: {
    name: 'Fan Burst',
    color: 0xffaa55,
    maxLevel: 6,
    descs: [
      'Fan of 3 bolts upward.',
      '+2 bolts in the fan.',
      '+25% damage.',
      '+1 bolt, wider arc.',
      '+25% fire rate.',
      '+50% damage.',
    ],
    cooldown(level) {
      return 720 / (level >= 5 ? 1.25 : 1);
    },
    count(level) {
      return 3 + (level >= 2 ? 2 : 0) + (level >= 4 ? 1 : 0);
    },
    damage(level) {
      let d = 3;
      if (level >= 3) d *= 1.25;
      if (level >= 6) d *= 1.5;
      return d;
    },
    arc(level) {
      return level >= 4 ? 1.3 : 1.0;
    },
  },
  drone: {
    name: 'Orbital Drone',
    color: 0xb19cff,
    maxLevel: 6,
    descs: [
      'Drone orbits and rams enemies.',
      '+1 drone.',
      '+25% drone damage.',
      '+1 drone.',
      'Faster orbit.',
      '+1 drone, +25% damage.',
    ],
    count(level) {
      return 1 + (level >= 2 ? 1 : 0) + (level >= 4 ? 1 : 0) + (level >= 6 ? 1 : 0);
    },
    damage(level) {
      let d = 2;
      if (level >= 3) d *= 1.25;
      if (level >= 6) d *= 1.25;
      return d;
    },
    orbitSpeed(level) {
      return level >= 5 ? 3.6 : 2.4;
    },
    orbitRadius(level) {
      return 70;
    },
  },
  missile: {
    name: 'Homing Missile',
    color: 0xff7799,
    maxLevel: 6,
    descs: [
      'Fires a homing missile.',
      '+1 missile per salvo.',
      '+25% explosion damage.',
      '+1 missile per salvo.',
      '+25% fire rate.',
      'Larger blast radius.',
    ],
    cooldown(level) {
      return 1400 / (level >= 5 ? 1.25 : 1);
    },
    count(level) {
      return 1 + (level >= 2 ? 1 : 0) + (level >= 4 ? 1 : 0);
    },
    damage(level) {
      let d = 8;
      if (level >= 3) d *= 1.25;
      return d;
    },
    blastRadius(level) {
      return level >= 6 ? 70 : 50;
    },
  },
  shockwave: {
    name: 'Shockwave',
    color: 0x88ffaa,
    maxLevel: 6,
    descs: [
      'Pulses a damaging ring.',
      '+25% radius.',
      '+25% damage.',
      '+25% fire rate.',
      '+25% radius.',
      '+50% damage.',
    ],
    cooldown(level) {
      return 2200 / (level >= 4 ? 1.25 : 1);
    },
    damage(level) {
      let d = 6;
      if (level >= 3) d *= 1.25;
      if (level >= 6) d *= 1.5;
      return d;
    },
    radius(level) {
      let r = 120;
      if (level >= 2) r *= 1.25;
      if (level >= 5) r *= 1.25;
      return r;
    },
  },
  laser: {
    name: 'Beam Lance',
    color: 0xff66ee,
    maxLevel: 6,
    descs: [
      'Sweeping piercing beam.',
      '+50% beam width.',
      '+25% damage.',
      '+25% fire rate.',
      '+50% beam width.',
      '+50% damage.',
    ],
    cooldown(level) {
      return 1800 / (level >= 4 ? 1.25 : 1);
    },
    damage(level) {
      let d = 5;
      if (level >= 3) d *= 1.25;
      if (level >= 6) d *= 1.5;
      return d;
    },
    width(level) {
      let w = 14;
      if (level >= 2) w *= 1.5;
      if (level >= 5) w *= 1.5;
      return w;
    },
  },
};

// ---------- PASSIVES ----------
export const PASSIVES = {
  hp: {
    name: 'Hull Plating',
    desc: '+1 max HP and heal 1.',
    color: 0xff5566,
    maxLevel: 8,
    apply(p) { p.maxHp += 1; p.hp = Math.min(p.maxHp, p.hp + 1); },
  },
  damage: {
    name: 'Overcharger',
    desc: '+10% damage.',
    color: 0xffaa33,
    maxLevel: 10,
    apply(p) { p.damageMult *= 1.10; },
  },
  speed: {
    name: 'Thrusters',
    desc: '+10% move speed.',
    color: 0x77ddff,
    maxLevel: 6,
    apply(p) { p.moveSpeed *= 1.10; },
  },
  firerate: {
    name: 'Cooling Coils',
    desc: '+8% fire rate.',
    color: 0x99ff88,
    maxLevel: 8,
    apply(p) { p.fireRateMult *= 1.08; },
  },
  magnet: {
    name: 'Tractor Field',
    desc: '+30% pickup radius.',
    color: 0xddbbff,
    maxLevel: 5,
    apply(p) { p.pickupRadius *= 1.30; },
  },
  xp: {
    name: 'Data Mining',
    desc: '+15% XP gain.',
    color: 0xeeff88,
    maxLevel: 5,
    apply(p) { p.xpMult *= 1.15; },
  },
  crit: {
    name: 'Targeting Matrix',
    desc: '+8% crit chance.',
    color: 0xff77ff,
    maxLevel: 6,
    apply(p) { p.critChance += 0.08; },
  },
  regen: {
    name: 'Nanobots',
    desc: 'Regen 0.4 HP/sec.',
    color: 0x88ffcc,
    maxLevel: 5,
    apply(p) { p.regenPerSec += 0.4; },
  },
  pspeed: {
    name: 'Railgun Mods',
    desc: '+15% projectile speed.',
    color: 0xccccff,
    maxLevel: 5,
    apply(p) { p.projectileSpeedMult *= 1.15; },
  },
  armor: {
    name: 'Deflectors',
    desc: '-1 damage taken (min 1).',
    color: 0xaaaaaa,
    maxLevel: 3,
    apply(p) { p.armor += 1; },
  },
};

// ---------- ENEMIES ----------
export const ENEMY_TYPES = {
  grunt: {
    name: 'grunt',
    hp: 3, speed: 50, damage: 1, xp: 1, credits: 0.06,
    color: 0x66ff88, size: 18, ai: 'descend',
  },
  swarm: {
    name: 'swarm',
    hp: 1, speed: 95, damage: 1, xp: 1, credits: 0.04,
    color: 0xffff88, size: 12, ai: 'descend_wiggle',
  },
  tank: {
    name: 'tank',
    hp: 18, speed: 30, damage: 2, xp: 4, credits: 0.20,
    color: 0xff8844, size: 28, ai: 'descend',
  },
  shooter: {
    name: 'shooter',
    hp: 5, speed: 35, damage: 1, xp: 2, credits: 0.12,
    color: 0xff66ff, size: 20, ai: 'shoot',
    fireDelay: 1600,
  },
  diver: {
    name: 'diver',
    hp: 4, speed: 140, damage: 2, xp: 2, credits: 0.10,
    color: 0xff5555, size: 16, ai: 'dive',
  },
  drifter: {
    name: 'drifter',
    hp: 8, speed: 40, damage: 1, xp: 3, credits: 0.15,
    color: 0x99aaff, size: 22, ai: 'sweep',
  },
};

export const BOSS_TYPES = {
  mothership: {
    name: 'mothership',
    hp: 250, speed: 18, damage: 3, xp: 40, credits: 5,
    color: 0xff3366, size: 60, ai: 'boss',
    fireDelay: 900,
  },
  hivemind: {
    name: 'hivemind',
    hp: 400, speed: 14, damage: 3, xp: 70, credits: 8,
    color: 0xaa44ff, size: 70, ai: 'boss_spawn',
    fireDelay: 1500,
  },
};

// ---------- META (persistent) UPGRADES ----------
export const META_UPGRADES = {
  start_hp: {
    name: 'Reinforced Hull',
    desc: (lvl) => `Start runs with +${lvl} max HP.`,
    maxLevel: 10,
    cost: (lvl) => 10 + lvl * 8,
    apply(player, lvl) { player.maxHp += lvl; player.hp += lvl; },
  },
  start_damage: {
    name: 'Calibrated Cannons',
    desc: (lvl) => `+${lvl * 5}% damage at start.`,
    maxLevel: 10,
    cost: (lvl) => 12 + lvl * 10,
    apply(player, lvl) { player.damageMult *= 1 + lvl * 0.05; },
  },
  start_speed: {
    name: 'Boosted Thrusters',
    desc: (lvl) => `+${lvl * 4}% move speed at start.`,
    maxLevel: 5,
    cost: (lvl) => 14 + lvl * 12,
    apply(player, lvl) { player.moveSpeed *= 1 + lvl * 0.04; },
  },
  xp_boost: {
    name: 'Neural Uplink',
    desc: (lvl) => `+${lvl * 5}% XP gain.`,
    maxLevel: 10,
    cost: (lvl) => 15 + lvl * 10,
    apply(player, lvl) { player.xpMult *= 1 + lvl * 0.05; },
  },
  magnet_boost: {
    name: 'Magnetic Coils',
    desc: (lvl) => `+${lvl * 10}% pickup radius.`,
    maxLevel: 5,
    cost: (lvl) => 12 + lvl * 8,
    apply(player, lvl) { player.pickupRadius *= 1 + lvl * 0.10; },
  },
  credit_boost: {
    name: 'Salvage Drones',
    desc: (lvl) => `+${lvl * 8}% credits from runs.`,
    maxLevel: 10,
    cost: (lvl) => 20 + lvl * 14,
    apply(player, lvl) { player.creditMult *= 1 + lvl * 0.08; },
  },
  reroll: {
    name: 'Tactical Database',
    desc: (lvl) => `${lvl} free upgrade-screen reroll(s) per run.`,
    maxLevel: 3,
    cost: (lvl) => 30 + lvl * 25,
    apply(player, lvl) { player.rerolls += lvl; },
  },
  start_spread: {
    name: 'Loadout: Fan Burst',
    desc: () => 'Begin runs with Fan Burst.',
    maxLevel: 1,
    cost: () => 40,
    apply(player) { player.startingWeapons.push('spread'); },
  },
  start_drone: {
    name: 'Loadout: Orbital Drone',
    desc: () => 'Begin runs with an Orbital Drone.',
    maxLevel: 1,
    cost: () => 50,
    apply(player) { player.startingWeapons.push('drone'); },
  },
  start_missile: {
    name: 'Loadout: Homing Missile',
    desc: () => 'Begin runs with Homing Missiles.',
    maxLevel: 1,
    cost: () => 65,
    apply(player) { player.startingWeapons.push('missile'); },
  },
  revive: {
    name: 'Black Box',
    desc: () => 'Revive once per run at 50% HP.',
    maxLevel: 1,
    cost: () => 80,
    apply(player) { player.reviveTokens += 1; },
  },
};
